import React from 'react';
import { X } from 'lucide-react';

const PrivacyPolicy = ({ onClose }) => {
  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl max-w-4xl max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-teal-500 text-white p-6 flex items-center justify-between rounded-t-3xl">
          <h2 className="text-3xl font-black">Privacy Policy</h2>
          <button 
            onClick={onClose}
            className="p-2 hover:bg-white/20 rounded-full transition-colors"
            aria-label="Close"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
        
        <div className="p-8 space-y-6 text-gray-800">
          <p className="text-sm text-gray-600">
            <strong>Last Updated:</strong> October 23, 2025
          </p>
          
          <section>
            <h3 className="text-2xl font-bold text-teal-600 mb-3">Introduction</h3>
            <p className="leading-relaxed">
              Hope Cathedral ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, and safeguard your personal information when you visit our website or interact with our services.
            </p>
          </section>

          <section>
            <h3 className="text-2xl font-bold text-teal-600 mb-3">Information We Collect</h3>
            <p className="leading-relaxed mb-3">We collect the following types of information:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Personal Information:</strong> When you submit a pledge or contact form, we collect your name, email address, and optionally your company/organization name.</li>
              <li><strong>Pledge Information:</strong> Amount pledged and selected support tier.</li>
              <li><strong>Local Storage:</strong> Our chat widget uses browser localStorage to save your chat history locally on your device for convenience.</li>
              <li><strong>Automatically Collected Information:</strong> We do not currently use analytics or tracking cookies.</li>
            </ul>
          </section>

          <section>
            <h3 className="text-2xl font-bold text-teal-600 mb-3">How We Use Your Information</h3>
            <p className="leading-relaxed mb-3">We use the information we collect to:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Process and acknowledge your pledges and support</li>
              <li>Send you updates about Hope Cathedral production and screening events</li>
              <li>Respond to your inquiries and provide customer support</li>
              <li>Improve our website and services</li>
              <li>Comply with legal obligations</li>
            </ul>
          </section>

          <section>
            <h3 className="text-2xl font-bold text-teal-600 mb-3">How We Share Your Information</h3>
            <p className="leading-relaxed mb-3">
              We use <strong>Web3Forms</strong> to process form submissions. When you submit a pledge or contact form, your information is transmitted to Web3Forms and then forwarded to our email address (hopecathedralshow@gmail.com).
            </p>
            <p className="leading-relaxed">
              We do not sell, rent, or share your personal information with third parties for marketing purposes. We may share your information only in the following circumstances:
            </p>
            <ul className="list-disc pl-6 space-y-2 mt-3">
              <li>With service providers who help us operate our website (e.g., Web3Forms)</li>
              <li>When required by law or to protect our legal rights</li>
              <li>With your explicit consent</li>
            </ul>
          </section>

          <section>
            <h3 className="text-2xl font-bold text-teal-600 mb-3">Data Storage and Security</h3>
            <p className="leading-relaxed">
              Form submissions are processed through Web3Forms and delivered to our secure email. We implement reasonable security measures to protect your information, but please note that no method of transmission over the internet is 100% secure.
            </p>
          </section>

          <section>
            <h3 className="text-2xl font-bold text-teal-600 mb-3">Your Rights</h3>
            <p className="leading-relaxed mb-3">You have the right to:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Access the personal information we hold about you</li>
              <li>Request correction of inaccurate information</li>
              <li>Request deletion of your personal information</li>
              <li>Object to or restrict certain processing of your data</li>
              <li>Withdraw consent at any time</li>
            </ul>
            <p className="leading-relaxed mt-3">
              To exercise these rights, please contact us at <a href="mailto:hopecathedralshow@gmail.com" className="text-orange-500 hover:text-orange-600 font-semibold">hopecathedralshow@gmail.com</a>
            </p>
          </section>

          <section>
            <h3 className="text-2xl font-bold text-teal-600 mb-3">Cookies and Local Storage</h3>
            <p className="leading-relaxed">
              Our website uses browser localStorage (not cookies) solely for the chat widget functionality to preserve your conversation history locally on your device. This is strictly necessary for the feature to work and does not track you across websites.
            </p>
          </section>

          <section>
            <h3 className="text-2xl font-bold text-teal-600 mb-3">Children's Privacy</h3>
            <p className="leading-relaxed">
              Our website is not directed to children under 13. We do not knowingly collect personal information from children under 13. If you believe we have collected information from a child under 13, please contact us immediately.
            </p>
          </section>

          <section>
            <h3 className="text-2xl font-bold text-teal-600 mb-3">Changes to This Policy</h3>
            <p className="leading-relaxed">
              We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date.
            </p>
          </section>

          <section>
            <h3 className="text-2xl font-bold text-teal-600 mb-3">Contact Us</h3>
            <p className="leading-relaxed">
              If you have any questions about this Privacy Policy or our data practices, please contact us:
            </p>
            <div className="mt-3 p-4 bg-teal-50 rounded-xl">
              <p><strong>Email:</strong> <a href="mailto:hopecathedralshow@gmail.com" className="text-orange-500 hover:text-orange-600">hopecathedralshow@gmail.com</a></p>
              <p className="mt-2"><strong>Production Company:</strong> Dunamis - On-Stage Productions</p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
